<template>
  <v-app-bar app flat :color="themeColor" elevation="1" class="appHeader" :height="headerHeight">
    <div class="headerSide headerSideStart">
      <v-btn icon variant="text" class="headerIconBtn headerMenuBtn" @click="toggleNav">
        <v-icon color="white">mdi-menu</v-icon>
      </v-btn>
      <router-link v-if="isMobileLayout" to="/dashboard" class="headerLogoLink headerLogoLinkMobile">
        <img alt="MyCo+" class="headerLogo headerLogoMobile" :src="inverseLogoImg" />
      </router-link>
    </div>

    <div v-if="!isMobileLayout" class="headerCenter">
      <router-link to="/dashboard" class="headerLogoLink">
        <img alt="MyCo+" class="headerLogo" :src="inverseLogoImg" />
      </router-link>
    </div>

    <div class="headerSide headerSideEnd">
      <v-btn icon variant="text" class="headerIconBtn" :to="documentationRoute" title="Help">
        <v-icon color="white">mdi-help-circle-outline</v-icon>
      </v-btn>

      <v-btn icon variant="text" class="headerIconBtn" title="Search" @click="searchOpen = true">
        <v-icon color="white">mdi-magnify</v-icon>
      </v-btn>

      <v-menu location="bottom end" :close-on-content-click="false" @update:model-value="onNotificationsMenu">
        <template #activator="{ props }">
          <v-btn icon variant="text" class="headerIconBtn" v-bind="props">
            <v-badge v-if="notificationUnread" :content="notificationBadge" color="warning">
              <v-icon color="white">mdi-bell-outline</v-icon>
            </v-badge>
            <v-icon v-else color="white">mdi-bell-outline</v-icon>
          </v-btn>
        </template>
        <div class="headerDropdown bg-white">
          <div class="headerDropdownHeader">
            <span>You have {{ notificationUnread }} unread notifications</span>
            <a href="#" class="appBreadcrumbLink" @click.prevent="markAllNotifications">Mark all as read</a>
          </div>
          <div
            v-for="n in notifications"
            :key="n.id"
            class="headerDropdownItem"
            :class="{ headerDropdownItemClickable: notificationIsClickable(n) }"
            @click="openNotification(n)"
          >
            <div class="recentActivityAvatar">
              <v-avatar size="32" color="grey-lighten-3">
                <v-img v-if="notificationAvatar(n)" :src="notificationAvatar(n)" alt="" cover />
                <v-icon v-else size="20">mdi-account</v-icon>
              </v-avatar>
            </div>
            <div class="headerDropdownItemBody">
              <div class="text-body-2">{{ n.message }}</div>
              <div class="text-caption text-medium-emphasis">{{ formatTime(n.created_at) }}</div>
            </div>
            <v-btn
              v-if="n.status !== 'read'"
              icon
              size="x-small"
              variant="text"
              color="primary"
              title="Mark as read"
              @click.stop="markRead(n)"
            >
              <v-icon size="18">mdi-check</v-icon>
            </v-btn>
          </div>
          <div v-if="!notifications.length" class="recentActivityEmpty">No notifications</div>
          <div class="headerDropdownFooter">
            <router-link to="/notifications">View all</router-link>
          </div>
        </div>
      </v-menu>

      <v-menu location="bottom end" :close-on-content-click="false" @update:model-value="onRemindersMenu">
        <template #activator="{ props }">
          <v-btn icon variant="text" class="headerIconBtn" v-bind="props">
            <v-badge v-if="reminderUnread" :content="reminderBadge" color="warning">
              <v-icon color="white">mdi-flag-outline</v-icon>
            </v-badge>
            <v-icon v-else color="white">mdi-flag-outline</v-icon>
          </v-btn>
        </template>
        <div class="headerDropdown bg-white">
          <div class="headerDropdownHeader">
            <span>You have {{ reminderUnread }} unread reminders</span>
            <a href="#" class="appBreadcrumbLink" @click.prevent="markAllReminders">Mark all as read</a>
          </div>
          <div
            v-for="n in reminders"
            :key="n.id"
            class="headerDropdownItem headerDropdownItemClickable"
            @click="openReminder(n)"
          >
            <div class="recentActivityAvatar">
              <v-icon size="20">mdi-flag</v-icon>
            </div>
            <div class="headerDropdownItemBody">
              <div class="text-body-2">{{ n.message }}</div>
              <div class="text-caption text-medium-emphasis">{{ n.category }}</div>
            </div>
            <v-btn
              v-if="n.status !== 'read'"
              icon
              size="x-small"
              variant="text"
              color="primary"
              title="Mark as read"
              @click.stop="markRead(n)"
            >
              <v-icon size="18">mdi-check</v-icon>
            </v-btn>
          </div>
          <div v-if="!reminders.length" class="recentActivityEmpty">No reminders</div>
          <div class="headerDropdownFooter">
            <router-link to="/reminders">View all</router-link>
          </div>
        </div>
      </v-menu>

      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn variant="text" :icon="isMobileLayout" class="headerUserBtn text-none" v-bind="props">
            <v-avatar :size="avatarSize" color="white" :class="{ 'mr-2': !isMobileLayout }">
              <v-img v-if="headerAvatarUrl" :src="headerAvatarUrl" alt="" cover />
              <v-icon v-else color="primary">mdi-account</v-icon>
            </v-avatar>
            <span v-if="!isMobileLayout" class="headerUserName">{{ user?.name }}</span>
          </v-btn>
        </template>
        <v-list>
          <v-list-item :title="user?.name" :subtitle="user?.email" />
          <v-list-item title="Profile" to="/profile" />
          <v-list-item title="Log out" @click="logout" />
        </v-list>
      </v-menu>
    </div>

    <GlobalSearchModal v-model="searchOpen" />
  </v-app-bar>
</template>

<script>
import { useDisplay } from 'vuetify'
import { mapActions, mapGetters, mapState } from 'vuex'
import AuthAPI from '../../api/auth'
import { resetAuthBootstrap } from '../../api/authBootstrap'
import API from '../../api/myco'
import inverseLogoImg from '../../assets/inverse-logo.png'
import { formatActivityTime } from '../../utils/companySettings'
import { messageForApiError } from '../../utils/apiErrorMessage'
import { resolveAvatarUrl } from '../../utils/avatarUrl'
import { showErrorSnackbar, showSuccessSnackbar } from '../../utils/snackbar'
import {
  notificationRoute,
  openSubmissionPdfAndList,
  parseNotificationProperties,
  reminderRoute,
} from '../../utils/notificationLinks'
import { requestDesktopNotificationPermission } from '../../utils/notificationPolling'
import GlobalSearchModal from './GlobalSearchModal.vue'

export default {
  name: 'AppHeader',
  components: { GlobalSearchModal },
  setup() {
    const { mdAndDown } = useDisplay()
    return { mdAndDown }
  },
  data() {
    return {
      inverseLogoImg,
      searchOpen: false,
    }
  },
  computed: {
    ...mapState(['user', 'notifications', 'reminders']),
    ...mapGetters(['themeColor', 'isAdmin']),
    isMobileLayout() {
      return this.mdAndDown
    },
    headerHeight() {
      return 50
    },
    avatarSize() {
      return this.isMobileLayout ? 30 : 32
    },
    headerAvatarUrl() {
      return resolveAvatarUrl(this.user)
    },
    documentationRoute() {
      return this.isAdmin ? { name: 'help-admin' } : { name: 'help-regular' }
    },
    notificationUnread() {
      return this.notifications.filter((n) => n.status !== 'read').length
    },
    reminderUnread() {
      return this.reminders.filter((n) => n.status !== 'read').length
    },
    notificationBadge() {
      return this.notificationUnread > 999 ? '999+' : this.notificationUnread
    },
    reminderBadge() {
      return this.reminderUnread > 999 ? '999+' : this.reminderUnread
    },
  },
  methods: {
    ...mapActions(['refreshNotifications']),
    toggleNav() {
      if (this.isMobileLayout) {
        this.$store.commit('toggleNavMobileOpen')
      } else {
        this.$store.commit('toggleNavCollapsed')
      }
    },
    formatTime(ts) {
      return formatActivityTime(ts)
    },
    notificationAvatar(n) {
      const props = parseNotificationProperties(n?.properties)
      return resolveAvatarUrl({ ...props, avatarUrl: props.avatarUrl || props.avatar_url })
    },
    onNotificationsMenu(open) {
      if (open) requestDesktopNotificationPermission()
    },
    onRemindersMenu(open) {
      if (open) requestDesktopNotificationPermission()
    },
    notificationIsClickable(n) {
      const action = notificationRoute(n)
      return !!(action.href || action.route)
    },
    async openNotification(n) {
      openSubmissionPdfAndList(n, this.$router)
      if (n.status !== 'read') {
        await this.markRead(n)
      }
    },
    async openReminder(n) {
      this.$router.push(reminderRoute(n))
      if (n.status !== 'read') {
        await this.markRead(n)
      }
    },
    async markRead(n) {
      try {
        await API.notification.update({ ...n, status: 'read' })
        await this.refreshNotifications()
      } catch (err) {
        showErrorSnackbar(this.$store, messageForApiError(err, 'Could not mark notification as read.'), { error: err })
      }
    },
    async markAllNotifications() {
      try {
        await API.notification.markAllRead({ type: 'notifications' })
        await this.refreshNotifications()
        showSuccessSnackbar(this.$store, 'All notifications marked as read.')
      } catch (err) {
        showErrorSnackbar(this.$store, messageForApiError(err, 'Could not mark notifications as read.'), { error: err })
      }
    },
    async markAllReminders() {
      try {
        await API.notification.markAllRead({ type: 'reminders' })
        await this.refreshNotifications()
        showSuccessSnackbar(this.$store, 'All reminders marked as read.')
      } catch (err) {
        showErrorSnackbar(this.$store, messageForApiError(err, 'Could not mark reminders as read.'), { error: err })
      }
    },
    async logout() {
      try {
        await AuthAPI.logout()
      } catch {
        // ignore
      }
      resetAuthBootstrap()
      this.$store.commit('setUser', null)
      this.$store.commit('setCompany', null)
      this.$router.push('/login')
    },
  },
}
</script>

<style scoped>
.headerIconBtn {
  color: #fff;
}
.headerSide {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.headerSideStart {
  gap: 4px;
  min-width: 0;
}
.headerSideEnd {
  justify-content: flex-end;
}
.headerCenter {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  padding: 0 8px;
}
.headerLogoLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
}
.headerLogoLinkMobile {
  margin-left: 2px;
  min-width: 0;
  flex: 1;
}
.headerLogo {
  height: 27px;
  max-width: 100%;
  object-fit: contain;
}
.headerMenuBtn {
  flex-shrink: 0;
}
.headerUserBtn {
  color: #fff !important;
  margin-right: 8px;
}
.headerUserName {
  color: #fff;
  font-weight: 600;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 959px) {
  .headerSideStart {
    flex: 1;
    overflow: hidden;
    padding-right: 8px;
  }
  .headerLogoMobile {
    height: 26px;
    width: auto;
    max-width: 100%;
  }
  .headerUserBtn {
    margin-right: 2px;
  }
  .headerIconBtn {
    width: 36px;
    height: 36px;
  }
}
</style>

<style>
.appHeader .v-toolbar__content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  position: relative;
}
@media (max-width: 959px) {
  .appHeader .v-toolbar__content {
    display: flex;
    justify-content: space-between;
    padding-inline: 2px;
    gap: 0;
  }
  .appHeader .v-badge__badge {
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
  }
}
</style>
